const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1';
const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || 'http://localhost:8000/storage';

// Default TTL = 1 hour
const CACHE_TTL_DEFAULT = 3600000;

// Per-endpoint TTLs (in milliseconds) – tune these based on how frequently content changes
const CACHE_TTL_BY_ENDPOINT = {
  '/website/about':           3600000,   // 1 hour – very static
  '/website/contact':         3600000,
  '/website/faq':             3600000,
  '/website/return-policy':   3600000,
  '/website/social-links':    3600000,
  '/website/team':            3600000,
  '/website/services':        3600000,
  '/website/testimonials':    1800000,   // 30 min – might change occasionally
  '/website/hero':            1800000,   // banners
  '/website/theme-config':    900000,    // 15 min – theme affects whole site
  '/website/payment-config':  900000,    // 15 min – payment related
  '/website/settings':        900000,    // 15 min – critical site settings
  '/get-web-products':        300000,    // 5 min – products change more often
  // Add more endpoints here as needed
};

export { STORAGE_URL };

/**
 * Core fetch function with stale-while-revalidate caching
 * - Returns cached data immediately if available (fast UI)
 * - Revalidates in background and updates cache
 * - Falls back to stale cache on network failure
 * - Different 
 * TTLs per endpoint
 */
export async function fetchData(endpoint, options = {}) {
  const cacheKey = `cache_${endpoint.replace(/[^a-zA-Z0-9]/g, '_')}`;
  const ttl = CACHE_TTL_BY_ENDPOINT[endpoint] ?? CACHE_TTL_DEFAULT;

  // 1. Try to serve stale cache immediately (fast first paint)
  let cached = null;
  if (!options.noCache) {
    const cachedStr = localStorage.getItem(cacheKey);
    if (cachedStr) {
      try {
        cached = JSON.parse(cachedStr);
        // Serve even if slightly stale (up to 2× TTL window)
        if (Date.now() - cached.timestamp < ttl * 2) {
          console.log(`[cache hit] Serving data for ${endpoint} (age: ${(Date.now() - cached.timestamp)/1000/60 | 0} min)`);
          // We will return it right away, but still try to revalidate below
        } else {
          console.log(`[cache stale] Found expired cache for ${endpoint}`);
        }
      } catch (e) {
        console.warn(`[cache invalid] Clearing bad cache for ${endpoint}`);
        localStorage.removeItem(cacheKey);
      }
    }
  }

  

  // 2. Always attempt to fetch fresh data (background revalidation)
  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, {
      method: options.method || 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Client: process.env.REACT_APP_SUBDOMAIN,
        ...options.headers,
      },
      
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    

const responseData = await response.json().catch(() => null);

if (!response.ok) {
  const error = new Error(
    responseData?.message || `HTTP error! status: ${response.status}`
  );

  error.response = {
    status: response.status,
    data: responseData,
  };

  throw error;
}

    const freshData = responseData;

    // 3. Update cache with fresh data (unless explicitly disabled)
    if (!options.noCache) {
      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          data: freshData,
          timestamp: Date.now(),
          ttl,
        })
      );
      console.log(`[cache updated] Fresh data saved for ${endpoint}`);
    }

    // Return the fresh data
    return freshData;

  } catch (error) {
    console.error(`[fetch error] ${url}:`, error);

    // 4. Fallback: return stale cache if we have it (graceful degradation)
    if (cached && cached.data) {
      console.warn(`[fallback] Network failed – using stale cache for ${endpoint}`);
      return cached.data;
    }

    // No cache available → throw error to let caller handle
    throw error;
  }
}

export async function forgotPassword(payload) {
  return fetchData('/forgot-password', {
    method: 'POST',
    body: payload,
    noCache: true,
  });
}

export async function fetchCustomerProfile(token) {
  return fetchData('/customer/profile', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    noCache: true,
  });
}

export async function updateCustomerProfile(payload, token) {
  return fetchData('/customer/profile', {
    method: 'POST',
    body: payload,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    noCache: true,
  });
}

export async function fetchCustomerDashboardReport(token) {
  return fetchData('/reseller/customer-dashboard-report', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    noCache: true,
  });
}

export async function fetchCountries() {
  return fetchData('/countries', {
    method: 'GET',
    noCache: true,
  });
}

export async function fetchStates(countryId) {
  return fetchData(`/countries/${countryId}/states`, {
    method: 'GET',
    noCache: true,
  });
}

export async function resetPassword(payload) {
  return fetchData('/reset-password', {
    method: 'POST',
    body: payload,
    noCache: true,
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// Specific API functions – most now rely on cache with appropriate TTL
// ──────────────────────────────────────────────────────────────────────────────

export async function fetchNgmktSell(payload, merchantSubdomain) {
  return fetchData(`/sell-online?shop_id=1`, {
    method: 'POST',
    body: payload,
    headers: merchantSubdomain
      ? { Client: merchantSubdomain }
      : undefined,
    noCache: true, // Never cache mutations/transactions
  });
}

export async function fetchCustomerOrderDeliveryStatus(order) {
  const merchant = order?.merchant || {};
  let endpoint =
    merchant.transaction_detail_endpoint ||
    merchant.endpoints?.transaction_detail;
  const visibilityToken =
    merchant.auth_token ||
    merchant.authentication_token;

  if (!endpoint || !order?.uuid) {
    throw new Error(
      'Live delivery tracking is not configured for this restaurant.'
    );
  }

  endpoint = endpoint.includes(':uuid')
    ? endpoint.replace(':uuid', encodeURIComponent(order.uuid))
    : `${endpoint.replace(/\/$/, '')}/${encodeURIComponent(order.uuid)}`;

  const headers = {
    Accept: 'application/json',
    Client: merchant.subdomain,
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };

  if (visibilityToken) {
    headers.Authorization = `Bearer ${visibilityToken}`;
    headers['visibility-token'] = visibilityToken;
    headers['X-Visibility-Token'] = visibilityToken;
  }

  const response = await fetch(endpoint, {
    method: 'GET',
    headers,
    cache: 'no-store',
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(
      payload?.message ||
        `Delivery request failed with status ${response.status}.`
    );
    error.response = {
      status: response.status,
      data: payload,
    };
    throw error;
  }

  return payload?.data || payload;
}

export async function createResellerLog(payload, customerToken = null) {
  return fetchData('/reseller/logs', {
    method: 'POST',
    body: payload,
    headers: {
      Client:
        process.env.REACT_APP_RESELLER_SUBDOMAIN ||
        process.env.REACT_APP_SUBDOMAIN,
      ...(customerToken
        ? { Authorization: `Bearer ${customerToken}` }
        : {}),
    },
    noCache: true,
  });
}

export async function updateResellerLog(logId, payload) {
  return fetchData(`/reseller/logs/${logId}`, {
    method: 'PATCH',
    body: payload,
    headers: {
      Client:
        process.env.REACT_APP_RESELLER_SUBDOMAIN ||
        process.env.REACT_APP_SUBDOMAIN,
    },
    noCache: true,
  });
}

export async function createAccount(payload) {
  return fetchData('/store-tenant', {
    method: 'POST',
    body: payload,
    noCache: true,
  });
}

export async function confirmEmail(payload) {
  return fetchData('/confirm-email', {
    method: 'POST',
    body: payload,
    noCache: true,
  });
}

export async function resendEmailConfirmation(payload) {
  return fetchData('/resend-email-confirmation', {
    method: 'POST',
    body: payload,
    noCache: true,
  });
}
export async function fetchAbout() {
  return fetchData('/website/about');
}

export async function fetchContact() {
  return fetchData('/website/contact');
}

export async function fetchFAQ() {
  return fetchData('/website/faq');
}

export async function fetchHeroBanners() {
  const data = await fetchData('/website/hero');
  return data.images;
}

export async function fetchReturnPolicy() {
  return fetchData('/website/return-policy');
}

export async function fetchThemeSettings() {
  return fetchData('/website/theme-config');
}

export async function fetchPaymentConfig(merchantSubdomain) {
  return fetchData('/website/payment-config', {
    method: 'GET',
    headers: merchantSubdomain
      ? { Client: merchantSubdomain }
      : undefined,
    noCache: true,
  });
}

export async function initializeMerchantPayment(
  payload,
  merchantSubdomain,
  endpoint = '/website/payments/initialize'
) {
  return fetchData(endpoint, {
    method: 'POST',
    body: payload,
    headers: { Client: merchantSubdomain },
    noCache: true,
  });
}

export async function verifyMerchantPayment(
  payload,
  merchantSubdomain,
  endpoint = '/website/payments/verify'
) {
  return fetchData(endpoint, {
    method: 'POST',
    body: payload,
    headers: { Client: merchantSubdomain },
    noCache: true,
  });
}

export async function fetchProducts() {
  return fetchData('/get-web-products');
}

export async function fetchSettings() {
  return fetchData('/website/settings');
}

export async function fetchSocialLinks() {
  const data = await fetchData('/website/social-links');
  return data.links;
}

export async function fetchTestimonials() {
  return fetchData('/website/testimonials?company_id=');
}

export async function fetchTeam() {
  return fetchData('/website/team?company_id=');
}

export async function fetchServices() {
  return fetchData('/website/services?company_id=');
}

// ──────────────────────────────────────────────────────────────────────────────
// Cache management helpers (call from admin panel after content updates)
// ──────────────────────────────────────────────────────────────────────────────

export function invalidateCache(endpoint) {
  const cacheKey = `cache_${endpoint.replace(/[^a-zA-Z0-9]/g, '_')}`;
  localStorage.removeItem(cacheKey);
  console.log(`[cache invalidated] Removed cache for ${endpoint}`);
}

// Add this function to the same API service file that contains fetchData().

export async function sendContactMessage(payload) {
  return fetchData('/website/contact/send', {
    method: 'POST',
    body: payload,
    noCache: true,
  });
}


export function invalidateCriticalCaches() {
  const critical = [
    '/website/payment-config',
    '/get-web-products',
    '/website/settings',
    '/website/theme-config',
    '/website/hero',
    // add others you consider high-priority
  ];

  critical.forEach(endpoint => invalidateCache(endpoint));
  console.log('[cache cleared] Critical web content caches invalidated');
}

export function clearAllCache() {
  Object.keys(localStorage)
    .filter(key => key.startsWith('cache_'))
    .forEach(key => localStorage.removeItem(key));
  console.log('[cache cleared] All web caches removed');
}

export async function fetchRestaurantOnboardingOptions() {
  return fetchData('/restaurant-onboarding/options', {
    method: 'GET',
    noCache: true,
  });
}

export async function onboardRestaurant(payload) {
  return fetchData('/restaurant-onboarding', {
    method: 'POST',
    body: payload,
    noCache: true,
  });
}

export async function fetchStoreMerchants(params = {}) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== '')
  ).toString();
  return fetchData(`/reseller/merchants${query ? `?${query}` : ''}`, { noCache: true });
}

export async function fetchStoreMerchant(subdomain) {
  const response = await fetchStoreMerchants({ search: subdomain, per_page: 100 });
  const merchants = Array.isArray(response?.data) ? response.data : [];
  const merchant = merchants.find(
    (item) => String(item.subdomain || '').toLowerCase() === String(subdomain || '').toLowerCase()
  );

  if (!merchant) {
    const error = new Error('Restaurant not found.');
    error.response = { status: 404, data: { message: 'Restaurant not found.' } };
    throw error;
  }

  return { data: merchant };
}

function unwrapMerchantResponse(payload, preferredKey) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.[preferredKey])) return payload[preferredKey];
  if (Array.isArray(payload?.data?.[preferredKey])) return payload.data[preferredKey];
  return [];
}

async function fetchMerchantEndpoint(merchant, endpointName, preferredKey, categoryId) {
  let endpoint = merchant?.endpoints?.[endpointName];
  if (!endpoint) {
    const error = new Error(`${preferredKey} endpoint is not configured for this restaurant.`);
    error.response = { status: 422, data: { message: error.message } };
    throw error;
  }

  if (categoryId !== undefined) {
    endpoint = endpoint.includes(':id')
      ? endpoint.replace(':id', encodeURIComponent(categoryId))
      : `${endpoint.replace(/\/$/, '')}/${encodeURIComponent(categoryId)}`;
  }

  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Client: merchant.subdomain,
  };

  if (merchant.auth_token) {
    headers.Authorization = `Bearer ${merchant.auth_token}`;
    headers['visibility-token'] = merchant.auth_token;
    headers['X-Visibility-Token'] = merchant.auth_token;
  }

  const response = await fetch(endpoint, { method: 'GET', headers });
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(payload?.message || `Merchant request failed with status ${response.status}.`);
    error.response = { status: response.status, data: payload };
    throw error;
  }

  return { data: unwrapMerchantResponse(payload, preferredKey), raw: payload };
}

export function fetchMerchantCategories(merchant) {
  return fetchMerchantEndpoint(merchant, 'categories', 'categories');
}

export function fetchMerchantProducts(merchant) {
  return fetchMerchantEndpoint(merchant, 'products', 'products');
}

export function fetchMerchantCategoryProducts(merchant, categoryId) {
  return fetchMerchantEndpoint(merchant, 'product_categories', 'products', categoryId);
}
